import { findKeyInObj } from "../../../../../common/utils/object";
import {
  ACTION_TYPE,
  ActionTarget,
  createEventAction,
  getFailActionName,
  getStartActionName,
  getSuccessActionName,
} from "./make-action";
import TopLevelMethod from "./TopLevelMethod";
import ViewerEvent from "./ViewerEvent";
import { generateUUID } from "../../../../../common/utils/uuid";
import { Action, AnyAction, Middleware, MiddlewareAPI } from "redux";
import { ApiActionRequest, ApiActionResponse } from "./action-types";
import { initLinkedBehaviors } from "./auto-run";
import processActionForEventEmission from "./action-emitter";
import { getApiMetaData, isApiMetaData } from "./ViewerApiMetaData";
import ViewerMethod from "./ViewerMethod";
import { noticeError } from "../../../../../services/monitoring";

let viewerCanProcessActions = false;
let pendingViewerActions: {
  action: any;
  resolve: (result) => void;
  uuid: string;
}[] = [];

export function reset() {
  viewerCanProcessActions = false;
  pendingViewerActions = [];
  window.Viewer = undefined;
}

function initViewerEvents(store) {
  Object.values(ViewerEvent).forEach((eventName) => {
    window.Viewer.listen(eventName, (eventData) => {
      store.dispatch(createEventAction(eventName)(eventData.detail));
    });
  });
}

function runPendingActions(store) {
  viewerCanProcessActions = true;
  pendingViewerActions.forEach(({ action, resolve, uuid }) => {
    return runAPI(action, store, uuid, true).then((result) => {
      resolve(result);
    });
  });
  pendingViewerActions = [];
}

function dispatchResponse(response, store, apiMethodName, apiCallArgs, uuid) {
  let action;
  if (response.ok) {
    action = {
      type: getSuccessActionName(apiMethodName),
      apiCallArgs,
      resultData: response.data,
      resultStatus: response.status,
      uuid,
    };
  } else {
    action = {
      type: getFailActionName(apiMethodName),
      apiCallArgs,
      resultStatus: response.status,
      uuid,
    };
  }
  store.dispatch(action);
  processActionForEventEmission(action);
}

function runAPI(action, store, uuid, skipStart = false) {
  const { apiMethodName, apiCallArgs, apiTarget } = action.mosaic;
  const targetRef = apiTarget === ActionTarget.VIEWER ? window.Viewer : window;
  const method = findKeyInObj(targetRef, apiMethodName);
  let apiMetaData;
  if (apiCallArgs.length) {
    const lastArg = apiCallArgs[apiCallArgs.length - 1];
    if (isApiMetaData(lastArg)) {
      apiMetaData = getApiMetaData(apiCallArgs.pop());
    }
  }
  if (!skipStart) {
    store.dispatch({
      type: getStartActionName(apiMethodName),
      apiCallArgs,
      uuid,
      ...(apiMetaData && { apiMetaData }),
    });
  }
  const result = method(...apiCallArgs);
  // Promises returned here are so that:
  //   1. Callers can show loading/error indicators, which are typically
  //      local-to-the-call and awkward to do in redux
  //   2. Multi-part thunk actions can wait for one step to complete before
  //      proceeding to another.
  if (result.then) {
    return result.then(
      (
        { ok, data, error, status } = {
          ok: true,
          data: undefined,
          status: undefined,
          error: undefined,
        }
      ) => {
        const isCreatePageViewer =
          apiMethodName === TopLevelMethod.CREATE_PAGE_VIEWER ||
          apiMethodName === TopLevelMethod.CREATE_SNIPPET_VIEWER;
        dispatchResponse(
          // Don't dispatch the viewer. It is full of functions and things.
          {
            ok,
            data: isCreatePageViewer ? undefined : data,
            status,
            error,
          },
          store,
          apiMethodName,
          apiCallArgs,
          uuid
        );
        if (isCreatePageViewer && ok) {
          window.Viewer = data;
          initViewerEvents(store);
          initLinkedBehaviors(window.Viewer, store);
          runPendingActions(store);
        }
        if (!ok) {
          const loggedMethods = [
            ViewerMethod.GET_PAGE_BREAKS,
            ViewerMethod.GET_BOOK_INFO,
            TopLevelMethod.GET_USER_INFO,
          ];
          if (
            loggedMethods.indexOf(apiMethodName) >= 0 &&
            status !== 401 && // not logged in
            status !== 403 // logged in, but no license
          ) {
            noticeError(`${apiMethodName} ${status} ${error}`);
          }
        }
        return { ok, data, error };
      }
    );
  } else {
    // Void returns exist and are ok
    return Promise.resolve({ ok: true });
  }
}

export interface MosaicDispatch<A extends Action = AnyAction> {
  (mosaicAction: ApiActionRequest): Promise<ApiActionResponse>;
  <T extends A>(action: T): T;
}

const mosaicMiddleware: Middleware = (store: MiddlewareAPI<MosaicDispatch>) => (
  next
) => (action) => {
  if (action.type !== ACTION_TYPE) {
    // Normal action: pass it on
    return next(action);
  }

  const { apiMethodName, apiCallArgs, apiTarget } = action.mosaic;
  const uuid = generateUUID();
  const startAction = {
    type: getStartActionName(apiMethodName),
    apiCallArgs,
    uuid,
  };
  if (apiTarget === ActionTarget.VIEWER && !viewerCanProcessActions) {
    store.dispatch(startAction);
    return new Promise((resolve) => {
      pendingViewerActions.push({ action, resolve, uuid });
    });
  } else {
    return runAPI(action, store, uuid);
  }
};

export default mosaicMiddleware;
